import { api } from '../../../../components/common';
import { Channel, CreateChannelRequest, UpdateChannelRequest, EmailMessageSummary, EmailMessage, EmailSyncResult, EmailMailbox, EmailReplyDraft, ReplyEmailResult, MoveEmailResult, ReadStateResult, AiSmartFolder, AiSyncResult } from '../types';

const mailboxQuery = (mailbox: string) => `mailbox=${encodeURIComponent(mailbox)}`;

export const channelsApi = {
  getAll: (): Promise<Channel[]> =>
    api.get('/communications/channels').then(res => res.data),

  getById: (id: number): Promise<Channel> =>
    api.get(`/communications/channels/${id}`).then(res => res.data),

  create: (data: CreateChannelRequest): Promise<Channel> =>
    api.post('/communications/channels', data).then(res => res.data),

  update: (id: number, data: UpdateChannelRequest): Promise<Channel> =>
    api.put(`/communications/channels/${id}`, data).then(res => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/communications/channels/${id}`).then(() => {})
};

export const channelTestApi = {
  test: (config: { host: string; port?: number; secure?: number; username: string; password?: string }): Promise<{ success: boolean; message: string }> =>
    api.post('/communications/test', config).then(res => res.data),

  testOAuth: (channelId: number): Promise<{ success: boolean; message: string }> =>
    api.post(`/communications/channels/${channelId}/oauth/test`).then(res => res.data)
};

export const oauthApi = {
  getConfig: (provider: 'google' | 'microsoft' = 'google'): Promise<{ provider: string; redirectUri: string; javascriptOrigin: string; scopes: string[] }> =>
    api.get('/communications/oauth/config', { params: { provider } }).then(res => res.data),

  getAuthorizeUrl: (channelId: number, provider: 'google' | 'microsoft'): Promise<{ url: string; provider: string; redirectUri: string; javascriptOrigin: string }> =>
    api.get(`/communications/channels/${channelId}/oauth/authorize`, { params: { provider } }).then(res => res.data),

  exchangeCode: (code: string, state: string): Promise<{ success: boolean; channelId: number; provider?: string }> =>
    api.post('/communications/oauth/exchange', { code, state }).then(res => res.data),

  disconnect: (channelId: number): Promise<void> =>
    api.delete(`/communications/channels/${channelId}/oauth`).then(() => {})
};

export const emailApi = {
  getMailboxes: (channelId: number): Promise<EmailMailbox[]> =>
    api.get(`/communications/channels/${channelId}/mailboxes`).then(res => res.data),

  getMessages: (channelId: number, mailbox: string, limit = 50): Promise<EmailMessageSummary[]> =>
    api.get(`/communications/channels/${channelId}/messages?${mailboxQuery(mailbox)}&limit=${limit}`).then(res => res.data),

  getMessage: (channelId: number, mailbox: string, uid: number): Promise<EmailMessage> =>
    api.get(`/communications/channels/${channelId}/messages/${uid}?${mailboxQuery(mailbox)}`).then(res => res.data),

  sync: (channelId: number, mailbox: string, limit = 50): Promise<EmailSyncResult> =>
    api.post(`/communications/channels/${channelId}/sync`, { mailbox, limit }).then(res => res.data),

  getReplyDraft: (channelId: number, mailbox: string, uid: number): Promise<EmailReplyDraft> =>
    api.get(`/communications/channels/${channelId}/messages/${uid}/reply-draft?${mailboxQuery(mailbox)}`).then(res => res.data),

  getReplyAllDraft: (channelId: number, mailbox: string, uid: number): Promise<EmailReplyDraft> =>
    api.get(`/communications/channels/${channelId}/messages/${uid}/reply-all-draft?${mailboxQuery(mailbox)}`).then(res => res.data),

  getForwardDraft: (channelId: number, mailbox: string, uid: number): Promise<EmailReplyDraft> =>
    api.get(`/communications/channels/${channelId}/messages/${uid}/forward-draft?${mailboxQuery(mailbox)}`).then(res => res.data),

  reply: (
    channelId: number,
    mailbox: string,
    uid: number,
    payload: { to: string; cc?: string; subject?: string; text: string; html?: string }
  ): Promise<ReplyEmailResult> =>
    api.post(`/communications/channels/${channelId}/messages/${uid}/reply`, { mailbox, ...payload }).then(res => res.data),

  forward: (
    channelId: number,
    mailbox: string,
    uid: number,
    payload: { to: string; subject?: string; text: string; html?: string }
  ): Promise<ReplyEmailResult> =>
    api.post(`/communications/channels/${channelId}/messages/${uid}/forward`, { mailbox, ...payload }).then(res => res.data),

  moveMessage: (
    channelId: number,
    mailbox: string,
    uid: number,
    destination: string
  ): Promise<MoveEmailResult> =>
    api.post(`/communications/channels/${channelId}/messages/${uid}/move`, { mailbox, destination }).then(res => res.data),

  deleteMessage: (
    channelId: number,
    mailbox: string,
    uid: number
  ): Promise<MoveEmailResult> =>
    api.post(`/communications/channels/${channelId}/messages/${uid}/delete`, { mailbox }).then(res => res.data),

  markRead: (
    channelId: number,
    mailbox: string,
    uid: number
  ): Promise<ReadStateResult> =>
    api.post(`/communications/channels/${channelId}/messages/${uid}/read`, { mailbox }).then(res => res.data),

  markUnread: (
    channelId: number,
    mailbox: string,
    uid: number
  ): Promise<ReadStateResult> =>
    api.post(`/communications/channels/${channelId}/messages/${uid}/unread`, { mailbox }).then(res => res.data)
};

const folderQuery = (folder: string) => `folder=${encodeURIComponent(folder)}`;

export const aiApi = {
  getSmartFolders: (channelId: number): Promise<AiSmartFolder[]> =>
    api.get(`/communications/channels/${channelId}/smart-folders`).then(res => res.data),

  getMessages: (channelId: number, folder: string, limit = 50): Promise<EmailMessageSummary[]> =>
    api.get(`/communications/channels/${channelId}/ai/messages?${folderQuery(folder)}&limit=${limit}`).then(res => res.data),

  getMessage: (channelId: number, uid: number): Promise<EmailMessage> =>
    api.get(`/communications/channels/${channelId}/ai/messages/${uid}`).then(res => res.data),

  sync: (channelId: number, folder: string, limit = 50): Promise<AiSyncResult> =>
    api.post(`/communications/channels/${channelId}/ai/sync`, { folder, limit }).then(res => res.data),

  getReplyDraft: (channelId: number, uid: number): Promise<EmailReplyDraft> =>
    api.get(`/communications/channels/${channelId}/ai/messages/${uid}/reply-draft`).then(res => res.data),

  getReplyAllDraft: (channelId: number, uid: number): Promise<EmailReplyDraft> =>
    api.get(`/communications/channels/${channelId}/ai/messages/${uid}/reply-all-draft`).then(res => res.data),

  getForwardDraft: (channelId: number, uid: number): Promise<EmailReplyDraft> =>
    api.get(`/communications/channels/${channelId}/ai/messages/${uid}/forward-draft`).then(res => res.data),

  reply: (
    channelId: number,
    uid: number,
    payload: { to: string; cc?: string; subject?: string; text: string; html?: string }
  ): Promise<ReplyEmailResult> =>
    api.post(`/communications/channels/${channelId}/ai/messages/${uid}/reply`, payload).then(res => res.data),

  forward: (
    channelId: number,
    uid: number,
    payload: { to: string; subject?: string; text: string; html?: string }
  ): Promise<ReplyEmailResult> =>
    api.post(`/communications/channels/${channelId}/ai/messages/${uid}/forward`, payload).then(res => res.data),

  moveMessage: (
    channelId: number,
    uid: number,
    destination: string
  ): Promise<MoveEmailResult> =>
    api.post(`/communications/channels/${channelId}/ai/messages/${uid}/move`, { destination }).then(res => res.data),

  deleteMessage: (channelId: number, uid: number): Promise<MoveEmailResult> =>
    api.post(`/communications/channels/${channelId}/ai/messages/${uid}/delete`, {}).then(res => res.data),

  markUnread: (channelId: number, uid: number): Promise<ReadStateResult> =>
    api.post(`/communications/channels/${channelId}/ai/messages/${uid}/unread`, {}).then(res => res.data),

  getOriginMailboxes: (channelId: number, uid: number): Promise<EmailMailbox[]> =>
    api.get(`/communications/channels/${channelId}/ai/messages/${uid}/origin-mailboxes`).then(res => res.data)
};
